import { useEffect, useRef } from 'react';

/**
 * Event-driven catch-up — fires `fn` ONLY when the tab returns to the
 * foreground or the network comes back online. NO periodic interval.
 *
 * Replaces the 30s safety-poll (Alex 2026-06-19: "ich möchte diese
 * Intervall-Updates nicht"). The realtime SSE bus is the live channel;
 * this just covers the two moments where the bus may have gone stale
 * while you weren't looking — coming back to the tab, or regaining
 * connectivity. Everything else flows through SSE events, and F5 is the
 * ultimate backstop. Does NOT fire on mount (callers own their initial
 * bootstrap fetch) so there's no double-fetch on load.
 */
export function useRefreshOnReturn(fn: () => void | Promise<void>) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    const onVisible = () => { if (!document.hidden) void fnRef.current(); };
    const onOnline = () => { void fnRef.current(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, []);
}
